import { API_BASE_URL } from "src/config/api";
import React, { useCallback, useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { alpha, useTheme } from '@mui/material/styles';
import { format } from 'date-fns';
import {
    Card,
    CardContent,
    CardActions,
    Box,
    Typography,
    LinearProgress,
    Button,
    Stack,
    CardHeader,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ConfirmationDialog from '../../../layouts/shared/ConfirmationDialog';

// CountUp animation hook
const useCountUp = (target, duration = 1500) => {
    const [count, setCount] = useState(0);

    useEffect(() => {
        let start = 0;
        const increment = target / (duration / 30); // updates every 30ms

        const timer = setInterval(() => {
            start += increment;
            if (start >= target) {
                clearInterval(timer);
                setCount(target);
            } else {
                setCount(Math.ceil(start));
            }
        }, 30);

        return () => clearInterval(timer);
    }, [target, duration]);

    return count;
};

// Animated remaining days display
const RemainingDays = ({ expiredOn }) => {
    const remaining = expiredOn
        ? Math.max(0, Math.ceil((new Date(expiredOn) - new Date()) / (1000 * 60 * 60 * 24)))
        : 0;

    const animatedCount = useCountUp(remaining);

    return (
        <Typography variant="body1" fontWeight="bold">
            Remaining Days:{' '}
            <Box component="span" color="primary.main">
                {animatedCount}
            </Box>
        </Typography>
    );
};

const UsageOverviewCard = () => {
    const theme = useTheme();
    const token = localStorage.getItem('token');
    const [runningPlan, setRunningPlan] = useState(null);
    const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);
    const [isRenewPlanDialogOpen, setIsRenewPlanDialogOpen] = useState(false);
    const [itemIdToDelete, setItemIdToDelete] = useState(null);
    const [itemIdToRenew, setItemIdToRenew] = useState(null);
    const navigate = useNavigate();
    const fetchPlan = useCallback(async () => {
        try {
            const cleanToken = token.replace(/"/g, '');
            const config = {
                headers: {
                    Authorization: `Bearer ${cleanToken}`,
                },
            };

            const response = await axios.get(`${API_BASE_URL}/userPlanOverview`, config);

            if (response.data && response.data.data) {
                const plans = Object.entries(response.data.data).map(([id, plan]) => ({
                    id,
                    ...plan,
                }));
                const activePlan = plans.length > 0 ? plans[0] : null;
                setRunningPlan(activePlan || null);
            } else {
                setRunningPlan(null);
            }
        } catch (error) {
            console.error('Error fetching running plan:', error);
        }
    }, [token]);

    useEffect(() => {
        fetchPlan();
    }, [fetchPlan]);

    const handleDeletePlan = (id) => {
        setItemIdToDelete(id);
        setIsConfirmationDialogOpen(true);
    };

    const handleRenewPlan = (id) => {
        setItemIdToRenew(id);
        setIsRenewPlanDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        setIsConfirmationDialogOpen(false);
        try {
            const cleanToken = token.replace(/"/g, '');
            const config = {
                headers: {
                    authorization: `Bearer ${cleanToken}`,
                },
            };
            const response = await axios.post(
                `${API_BASE_URL}/deleteSubscriptionPlan`,
                { purchaseSubscribeId: itemIdToDelete },
                config
            );
            fetchPlan();
            toast[response.data.success ? 'success' : 'error'](response.data.message);
        } catch (error) {
            toast.error('Failed to delete subscription plan');
        }
    };

    const handleConfirmRenew = async () => {
        setIsRenewPlanDialogOpen(false);
        try {
            const cleanToken = token.replace(/"/g, '');
            const config = {
                headers: {
                    authorization: `Bearer ${cleanToken}`,
                },
            };
            const response = await axios.post(
                `${API_BASE_URL}/renewSubscriptionPlan`,
                { purchaseSubscribeId: itemIdToRenew },
                config
            );
            fetchPlan();
            toast[response.data.success ? 'success' : 'error'](response.data.message);
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Renewal failed');
        }
    };

    return (
        <>
            <Card sx={{ height: '380px', display: 'flex', flexDirection: 'column' }}>
                <CardHeader title={'Active Plan Details'} />
                <CardContent sx={{ flexGrow: 1 }}>
                    {runningPlan ? (
                        <>
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                <Box>
                                    <Typography variant="subtitle1" color="textSecondary">
                                        {`${runningPlan.planName} Plan`}
                                    </Typography>
                                </Box>
                                <Box textAlign="right">
                                    {runningPlan.expiredOn && new Date(runningPlan.expiredOn) < new Date() ? (
                                        <Typography variant="body1" color="error" fontWeight="bold">
                                            Expired
                                        </Typography>
                                    ) : runningPlan.expiredOn ? (
                                        <RemainingDays expiredOn={runningPlan.expiredOn} />
                                    ) : null}
                                </Box>
                            </Box>

                            <Box sx={{ mt: 2, mb: 2 }}>
                                <Box display="flex" justifyContent="space-between">
                                    <Typography variant="body2">Node Used</Typography>
                                    <Typography variant="body2">
                                        {runningPlan.totalIpCount - runningPlan.ipCount} / {runningPlan.totalIpCount}
                                    </Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={
                                        runningPlan.totalIpCount
                                            ? Math.round(((runningPlan.totalIpCount - runningPlan.ipCount) / runningPlan.totalIpCount) * 100)
                                            : 0
                                    }
                                    sx={{ height: 10, borderRadius: 5, mt: 1 }}
                                />
                            </Box>

                            <Box sx={{ mb: 2 }}>
                                <Box display="flex" justifyContent="space-between">
                                    <Typography variant="body2">Domain Used</Typography>
                                    <Typography variant="body2">
                                        {runningPlan.totalDomainCount - runningPlan.domainCount} / {runningPlan.totalDomainCount}
                                    </Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={
                                        runningPlan.totalDomainCount
                                            ? Math.round(((runningPlan.totalDomainCount - runningPlan.domainCount) / runningPlan.totalDomainCount) * 100)
                                            : 0
                                    }
                                    sx={{ height: 10, borderRadius: 5, mt: 1 }}
                                />
                            </Box>

                            <Box
                                sx={{
                                    mt: 2,
                                    p: 1.5,
                                    borderRadius: 1,
                                    backgroundColor:
                                        theme.palette.mode === 'light'
                                            ? alpha(theme.palette.grey[200], 0.9) 
                                            : alpha(theme.palette.background.paper, 0.4),
                                }}
                            >
                                <Typography variant="body2" color="text.secondary">
                                    <strong>Buy Date:</strong>{' '}
                                    {format(new Date(runningPlan.renewDate), 'dd MMM yyyy')}
                                </Typography>

                                <Typography variant="body2" color="text.secondary" mt={1}>
                                    <strong>Expiry Date:</strong>{' '}
                                    {runningPlan.expiredOn
                                        ? format(new Date(runningPlan.expiredOn), 'dd MMM yyyy')
                                        : 'No Expiry'}
                                </Typography>
                            </Box>
                        </>
                    ) : (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: 230,
                                textAlign: 'center',
                            }}
                        >
                            <Typography variant="body1" color="text.secondary">
                                No active Subscription
                            </Typography>
                        </Box>
                    )}
                </CardContent>

                {runningPlan && (
                    <CardActions sx={{ justifyContent: 'flex-end', px: 3, pb: 3 }}>
                        <Stack direction="row" spacing={2}>
                            {runningPlan.expiredOn && new Date(runningPlan.expiredOn) < new Date() ? (
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    size="small"
                                    onClick={() => handleRenewPlan(runningPlan.id)}
                                >
                                    Renew Plan
                                </Button>
                            ) : (
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    size="small"
                                    onClick={() => {
                                        navigate('/dashboard/subscription');
                                    }}
                                >
                                    Upgrade Plan
                                </Button>
                            )}

                            <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                onClick={() => handleDeletePlan(runningPlan.id)}
                            >
                                Delete Plan
                            </Button>
                        </Stack>
                    </CardActions>
                )}
            </Card>

            <ConfirmationDialog
                isOpen={isConfirmationDialogOpen}
                onClose={() => setIsConfirmationDialogOpen(false)}
                onConfirm={handleConfirmDelete}
                message={
                    <>
                        <Typography variant="body1" gutterBottom>
                            Are you sure you want to delete this Plan?
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Note: Data associated with this subscription will be permanently deleted and cannot be refunded.
                        </Typography>
                    </>
                }
            />

            <ConfirmationDialog
                isOpen={isRenewPlanDialogOpen}
                onClose={() => setIsRenewPlanDialogOpen(false)}
                onConfirm={handleConfirmRenew}
                title="Renew Plan Confirmation"
                message="Are you sure you want to Renew this Plan?"
            />
        </>
    );
};

export default UsageOverviewCard;
